/**
 * Diff panel renderer — paints a "bulk update applied" card with an
 * undo affordance.
 *
 * Used by the AI when it applies a sweeping change (re-tier 80 members
 * by alignment, re-rate a stack of bills against an issue, archive a
 * batch of stale projects, etc.). The card surfaces what changed
 * row-by-row + offers a one-click undo that ships a message back to
 * the AI to run the reverse operation.
 *
 * The visual is intentionally consistent across the surfaces that
 * paint render_* cards (live canvas, dashboard feed).
 */

import { registerRenderer, type RendererProps } from "./registry.js";
import { ArrowRight, Pencil, Undo2 } from "lucide-react";

export interface DiffChange {
  entity_id?: string;
  entity_name?: string;
  field?: string;
  before?: unknown;
  after?: unknown;
}

export interface ReversibleAction {
  /** Human description of what the undo will do. */
  description?: string;
  /** Free-form payload the AI receives back so it can construct the
   *  reverse operation. Round-tripped verbatim through onConsume. */
  [k: string]: unknown;
}

export interface DiffPanelInput {
  title?: string;
  scope_summary?: string;
  changes?: DiffChange[];
  reversible_action?: ReversibleAction | null;
}

const MAX_VISIBLE_ROWS = 30;

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "(none)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function DiffPanel({ input, consumed, onConsume }: RendererProps<DiffPanelInput>) {
  const changes = Array.isArray(input.changes) ? input.changes : [];
  const visible = changes.slice(0, MAX_VISIBLE_ROWS);
  const moreCount = changes.length - visible.length;
  const reversible = input.reversible_action;

  const handleUndo = () => {
    if (consumed) return;
    onConsume?.({
      kind: "undo",
      payload: reversible ?? undefined,
      followupMessage: `undo: ${input.title || "last bulk update"}. The reversible_action you provided was ${JSON.stringify(reversible || {})}. Run the reverse operation now.`,
    });
  };

  return (
    <div
      className={`self-stretch border border-stone-200 rounded-xl shadow-card overflow-hidden bg-white ${consumed ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 border-b border-stone-200">
        <Pencil size={14} strokeWidth={1.75} className="text-stone-500 shrink-0" aria-hidden />
        <span className="text-sm font-semibold text-stone-900 flex-1">
          {input.title || "Bulk update applied"}
        </span>
      </div>
      {input.scope_summary && (
        <div className="px-3 py-1.5 text-xs text-stone-600 italic border-b border-stone-100">
          {input.scope_summary}
        </div>
      )}
      <div className="divide-y divide-stone-100">
        {visible.map((c, i) => (
          <div
            key={i}
            className="grid items-center gap-2 px-3 py-1.5 text-xs"
            style={{ gridTemplateColumns: "1.4fr 1fr 1fr auto 1fr" }}
            title={c.entity_id || ""}
          >
            <div className="font-medium text-stone-800 truncate">
              {c.entity_name || c.entity_id || "?"}
            </div>
            <div className="text-stone-500 truncate">{c.field || ""}</div>
            <div className="text-stone-500 line-through truncate">{fmt(c.before)}</div>
            <ArrowRight size={12} className="text-stone-400" aria-hidden />
            <div className="text-stone-900 font-medium truncate">{fmt(c.after)}</div>
          </div>
        ))}
        {moreCount > 0 && (
          <div className="px-3 py-1.5 text-xs italic text-stone-400 text-center">
            +{moreCount} more change{moreCount === 1 ? "" : "s"}
          </div>
        )}
      </div>
      {reversible && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-stone-200 bg-stone-50">
          <button
            onClick={handleUndo}
            disabled={consumed}
            className="text-xs font-semibold px-2.5 py-1 rounded-md bg-white border border-stone-300 text-stone-800 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <Undo2 size={12} aria-hidden /> Undo
          </button>
          {reversible.description && (
            <span className="text-[11px] text-stone-500 italic">
              {String(reversible.description)}
            </span>
          )}
          {consumed && (
            <span className="text-[11px] text-stone-400 italic ml-auto">
              undone
            </span>
          )}
        </div>
      )}
    </div>
  );
}

registerRenderer<DiffPanelInput>({
  tool: "render_diff_panel",
  label: "Bulk update diff with undo",
  Component: DiffPanel,
});

// Re-export so a consumer that imports this module also gets the type.
export { DiffPanel };
